import { ChangeEvent, ReactNode, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Typography, alpha } from "@mui/material";
import {
  DeleteOutlineOutlined,
  FileUploadOutlined,
  InsertDriveFileOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { UUID } from "crypto";
import * as Sentry from "@sentry/react";
import {
  useGetUrlToDownloadDocMutation,
  useLazyGetSignUrlToUploadQuery,
  usePatchDocumentMutation,
  usePostDocumentMutation,
  useUploadFileMutation,
} from "@api/documents";
import { useGetFilesCatalogQuery } from "@api/catalogs";
import { DocumentResponse, ErrorResponse } from "@interfaces/response";
import { PayloadDocument, PayloadSignUrl } from "@interfaces/request";
import { RootState } from "@store/store";
import { Badge, BadgeTone } from "./Badge";

interface Props {
  title: string;
  /** Acepta nodos para los documentos cuyo detalle el diseño lista en viñetas. */
  sub?: ReactNode;
  required?: boolean;
  /** Código del catálogo de documentos (`DOCUMENTS_CATALOG` / `file_category_code`). */
  fileCode: string;
  /** Documento ya cargado. El dueño del valor es el padre, igual que en el expediente legacy. */
  document?: DocumentResponse;
  /** Avisa el documento nuevo tras subir, o `undefined` tras eliminarlo. */
  onDocumentChange: (document: DocumentResponse | undefined) => void;
  /** Bloquea la card (el `disabled` que trae el documento del expediente). */
  disabled?: boolean;
  /** `person` para los documentos de una persona; por defecto son de la empresa. */
  uploadType?: "client" | "person";
  /** Cliente al que se cuelga el documento; por defecto, el de la solicitud en Redux. */
  clientDetailsId?: UUID;
  personId?: UUID;
  guaranteeId?: UUID;
  /** Oculta el encabezado (título, badge y subtítulo). Usado por `MultiUploadCard` para slots adicionales. */
  hideHeader?: boolean;
}

/** Estado visible del documento, derivado del `validated` que devuelve el backend. */
type Estado = "pendiente" | "revision" | "validado" | "rechazado";

const ESTADO_TONE: Record<Estado, BadgeTone> = {
  pendiente: "neutral",
  revision: "warn",
  validado: "ok",
  rechazado: "danger",
};

const ESTADO_LABEL: Record<Estado, string> = {
  pendiente: "Pendiente",
  revision: "En revisión",
  validado: "Validado",
  rechazado: "Rechazado",
};

const ACCEPT = "application/pdf,image/jpeg,image/png";

/** `.me-upload-title`: comparte tipografía entre el estado vacío y el lleno. */
export const UploadCardTitle = ({ title, required }: { title: string; required?: boolean }) => (
  <Typography
    sx={{
      fontFamily: "Outfit",
      fontWeight: 600,
      fontSize: 13.5,
      color: "primary.dark",
      flex: 1,
      minWidth: 0,
      textAlign: "left",
    }}
  >
    {title}
    {required && (
      <Typography component="span" sx={{ color: "error.main", ml: "4px" }}>
        *
      </Typography>
    )}
  </Typography>
);

/** `.me-upload-sub` */
export const UploadCardSub = ({ children }: { children: ReactNode }) => (
  <Typography component="div" sx={{ fontSize: 11.5, color: "text.disabled", lineHeight: 1.5, textAlign: "left" }}>
    {children}
  </Typography>
);

/**
 * Card de carga de un documento: encabezado con el título y el estado, la nota del documento y,
 * abajo, la zona de subida o el archivo ya cargado. Es un solo contenedor en los dos casos —
 * solo cambia el bloque de abajo— para que la card no cambie de forma al subir el archivo.
 *
 * Trae dentro la cadena real portada de `components/CustomUploadFileV2.tsx`:
 * `getSignUrlToUpload` → POST del archivo a S3 → `postDocument`, el borrado con
 * `patchDocument({ status: false })` y la vista del archivo con `getUrlToDownloadDoc`.
 *
 * Es controlada: el documento lo tiene el padre, que es quien sabe de dónde viene (del store del
 * expediente o del `getDocumentAppId` de su pantalla) y qué hacer cuando cambia.
 */
export const UploadCard = ({
  title,
  sub,
  required,
  fileCode,
  document,
  onDocumentChange,
  disabled,
  uploadType = "client",
  clientDetailsId,
  personId,
  guaranteeId,
  hideHeader = false,
}: Props) => {
  const application = useSelector((state: RootState) => state.register.application);

  const documentConfig = useGetFilesCatalogQuery();
  const [triggerGetSignUrl] = useLazyGetSignUrlToUploadQuery();
  const [triggerUploadFile] = useUploadFileMutation();
  const [triggerPostDocument] = usePostDocumentMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [triggerGetDownloadUrl] = useGetUrlToDownloadDocMutation();

  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  /**
   * TODO: los errores solo llegan a Sentry y a la consola; falta avisarle al usuario en pantalla.
   * El toast está planteado en `raw/Plan_toast_onboarding_v2.md`.
   */
  const reportarError = (accion: string, mensaje: string, error: unknown) => {
    Sentry.captureException(error, { extra: { component: `UploadCard-${accion}`, fileCode } });
    console.error(mensaje, error);
  };

  const fileCategory = documentConfig.data?.find((item) => item.code === fileCode);
  const cliente = clientDetailsId ?? (application.client_details_id as UUID);

  const estado: Estado = !document
    ? "pendiente"
    : document.validated === false
      ? "rechazado"
      : document.validated === true
        ? "validado"
        : "revision";

  /** Cadena completa: url firmada → archivo a S3 → registro del documento. */
  const subirArchivo = async (file: File) => {
    if (!fileCategory) return;

    try {
      setLoading(true);

      const payloadSignUrl: PayloadSignUrl = {
        upload_type: uploadType,
        file_name: file.name,
        regimen: application.tax_system_type.toLocaleLowerCase(),
        client_details_id: cliente,
        person_id: personId,
        guarantee_id: guaranteeId,
        file_category_id: fileCategory.id,
        loan_application_id: application.id,
      };

      const signUrl = await triggerGetSignUrl(payloadSignUrl).unwrap();

      const body = new FormData();
      body.append("key", signUrl.fields.key);
      body.append("AWSAccessKeyId", signUrl.fields.AWSAccessKeyId);
      body.append("x-amz-security-token", signUrl.fields["x-amz-security-token"]);
      body.append("policy", signUrl.fields.policy);
      body.append("signature", signUrl.fields.signature);
      body.append("file", file);

      await triggerUploadFile({ url: signUrl.url, body }).unwrap();

      const payloadDocument: PayloadDocument = {
        loan_application_id: application.id,
        file_category_id: fileCategory.id,
        client_details_id: uploadType === "client" ? cliente : undefined,
        person_id: uploadType === "person" ? personId : undefined,
        guarantee_id: guaranteeId,
        url: signUrl.fields.key,
        file_name: file.name,
      };

      onDocumentChange(await triggerPostDocument(payloadDocument).unwrap());
    } catch (errorSubida) {
      reportarError(
        "subirArchivo",
        `Sucedió algo inesperado y no pudimos cargar el archivo: ${file.name}`,
        errorSubida,
      );
    } finally {
      setLoading(false);
    }
  };

  const onSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.item(0);
    // Se limpia el input para que volver a elegir el mismo archivo dispare el `change`.
    event.target.value = "";

    if (!file) return;

    // Documento rechazado: primero se da de baja el anterior, igual que el legacy.
    if (document?.validated === false) {
      try {
        setLoading(true);
        await triggerPatchDocument({ documentId: document.id, body: { status: false } }).unwrap();
      } catch (errorBaja) {
        reportarError("bajaPrevia", "No pudimos reemplazar el archivo", errorBaja);
        setLoading(false);
        return;
      }
    }

    await subirArchivo(file);
  };

  const eliminarArchivo = async () => {
    if (!document) return;

    try {
      setLoading(true);
      await triggerPatchDocument({ documentId: document.id, body: { status: false } }).unwrap();
      onDocumentChange(undefined);
    } catch (errorBorrado) {
      // 403: el documento ya fue validado por operaciones. No es un fallo técnico, así que se
      // sincroniza el estado real para que la card deje de ofrecer eliminarlo.
      if ((errorBorrado as ErrorResponse).status === 403) {
        onDocumentChange({ ...document, validated: true });
        reportarError(
          "eliminarArchivo",
          "El archivo no pudo ser eliminado debido a que se encuentra validado",
          errorBorrado,
        );
      } else {
        reportarError("eliminarArchivo", "Ha ocurrido un error al eliminar el archivo", errorBorrado);
      }
    } finally {
      setLoading(false);
    }
  };

  const verArchivo = async () => {
    if (!document) return;

    try {
      const url = await triggerGetDownloadUrl(document.url).unwrap();
      window.open(url, "_blank");
    } catch (errorDescarga) {
      reportarError("verArchivo", "No pudimos abrir el archivo", errorDescarga);
    }
  };

  const bloqueado = loading || documentConfig.isFetching || Boolean(disabled);

  // Un documento validado ya no se puede eliminar: el backend responde 403.
  const acciones = [
    { label: "Ver", icon: <VisibilityOutlined sx={{ fontSize: 14 }} />, danger: false, onClick: verArchivo },
    ...(estado === "validado"
      ? []
      : [
          {
            label: "Eliminar",
            icon: <DeleteOutlineOutlined sx={{ fontSize: 14 }} />,
            danger: true,
            onClick: eliminarArchivo,
          },
        ]),
  ];

  /*
   * Sin borde ni radio propios: la card siempre se pinta dentro del `Paper` de su grupo, y el
   * marco extra dejaba una caja dentro de otra. El dropzone y el bloque del archivo conservan los
   * suyos, que son los que separan la zona de subida del encabezado.
   */
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        p: hideHeader ? "4px 8px" : "8px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Box
        component="input"
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onSelectFile}
        sx={{ display: "none" }}
      />

      {!hideHeader && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
          <UploadCardTitle title={title} required={required} />
          {sub && <UploadCardSub>{sub}</UploadCardSub>}
        </Box>
      )}

      <Box sx={{ mt: "1px" }}>
        {document && estado !== "rechazado" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              p: "10px 12px",
              bgcolor: "tokens.surface2",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 26,
                height: 30,
                borderRadius: "4px",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                color: "text.disabled",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InsertDriveFileOutlined sx={{ fontSize: 14 }} />
            </Box>

            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 12.5,
                color: "primary.dark",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {document.file_name}
            </Typography>

            <Badge tone={ESTADO_TONE[estado]}>{ESTADO_LABEL[estado]}</Badge>

            {loading ? (
              <CircularProgress size={16} sx={{ flexShrink: 0 }} />
            ) : (
              <Box sx={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                {acciones.map((accion) => (
                  <Box
                    key={accion.label}
                    component="button"
                    type="button"
                    aria-label={`${accion.label} ${title}`}
                    onClick={accion.onClick}
                    data-testid={`upload-${fileCode}-${accion.label.toLowerCase()}-button`}
                    sx={{
                      all: "unset",
                      color: "text.disabled",
                      cursor: "pointer",
                      p: "6px",
                      borderRadius: "6px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.06)",
                        color: accion.danger ? "#C43D3D" : "primary.dark",
                      },
                    }}
                  >
                    {accion.icon}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            disabled={bloqueado}
            onClick={() => inputRef.current?.click()}
            aria-label={`${estado === "rechazado" ? "Volver a cargar" : "Subir"} ${title}`}
            data-testid={`upload-${fileCode}-dropzone`}
            sx={(theme) => ({
              all: "unset",
              boxSizing: "border-box",
              width: "100%",
              cursor: bloqueado ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              p: "10px 12px",
              bgcolor: "tokens.surface2",
              borderRadius: "8px",
              border: "1.5px dashed",
              borderColor: "tokens.borderStrong",
              opacity: bloqueado ? 0.6 : 1,
              transition: "border-color .12s ease, background-color .12s ease",
              "&:hover": {
                borderColor: bloqueado ? "tokens.borderStrong" : theme.palette.primary.main,
                bgcolor: bloqueado ? "tokens.surface2" : alpha(theme.palette.primary.main, 0.06),
              },
            })}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                color: "primary.main",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {loading ? <CircularProgress size={16} /> : <FileUploadOutlined sx={{ fontSize: 16 }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "primary.dark" }}>
                {loading
                  ? "Subiendo el archivo…"
                  : estado === "rechazado"
                    ? "Vuelve a cargar el archivo"
                    : "Arrastra el archivo o haz clic para subirlo"}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.disabled" }}>PDF, JPG o PNG · máx. 10 MB</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
